import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import api from "../api/axios";

let currentDeviceToken = null;
const PUSH_OPT_IN_STORAGE_KEY = "vg_native_push_opt_in";
const PUSH_PREFERENCE_EVENT = "vg:native-push-preference-changed";

const readPushOptIn = () => {
  try {
    return window.localStorage.getItem(PUSH_OPT_IN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const hasStoredPushPreference = () => {
  try {
    return window.localStorage.getItem(PUSH_OPT_IN_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

const writePushOptIn = (enabled) => {
  try {
    if (enabled) {
      window.localStorage.setItem(PUSH_OPT_IN_STORAGE_KEY, "1");
    } else {
      window.localStorage.setItem(PUSH_OPT_IN_STORAGE_KEY, "0");
    }
  } catch {
    // Push can still work for the current session when storage is unavailable.
  }
};

const notifyPushPreferenceChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PUSH_PREFERENCE_EVENT));
};

const buildPushPreferenceState = ({
  supported = true,
  permission = "prompt",
  optedIn = readPushOptIn(),
} = {}) => ({
  supported,
  permission,
  optedIn,
  enabled: Boolean(supported && permission === "granted" && optedIn),
});

const registerNativeDeviceToken = async (
  token,
  { enableRoutineNotifications = false } = {}
) => {
  if (!token) return null;

  currentDeviceToken = token;
  const platform = Capacitor.getPlatform();
  const payload = { token, platform };
  if (enableRoutineNotifications) {
    payload.enable_routine_notifications = true;
  }
  const response = await api.post("/notifications/device-token/", payload);
  return response?.data || null;
};

export async function getNativePushPreferenceState() {
  if (!Capacitor.isNativePlatform()) {
    return buildPushPreferenceState({
      supported: false,
      permission: "unsupported",
      optedIn: false,
    });
  }

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const support = await FirebaseMessaging.isSupported();
  if (!support.isSupported) {
    return buildPushPreferenceState({
      supported: false,
      permission: "unsupported",
      optedIn: false,
    });
  }

  const permissions = await FirebaseMessaging.checkPermissions();
  const permission = permissions.receive || "prompt";
  if (permission !== "granted" && readPushOptIn()) {
    await unregisterCurrentPushToken({ disableRoutineNotifications: true }).catch(
      () => null
    );
    writePushOptIn(false);
    notifyPushPreferenceChanged();
  }
  return buildPushPreferenceState({
    permission,
  });
}

export async function unregisterCurrentPushToken(
  { disableRoutineNotifications = false } = {}
) {
  if (!Capacitor.isNativePlatform()) return;
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  let token = currentDeviceToken;
  let responseData = null;

  if (!token) {
    try {
      token = (await FirebaseMessaging.getToken())?.token || null;
    } catch {
      token = null;
    }
  }

  try {
    if (token || disableRoutineNotifications) {
      const response = await api.delete("/notifications/device-token/remove/", {
        data: {
          token: token || "",
          ...(disableRoutineNotifications
            ? { disable_routine_notifications: true }
            : {}),
        },
      });
      responseData = response?.data || null;
    }
  } finally {
    await FirebaseMessaging.deleteToken().catch(() => null);
    currentDeviceToken = null;
  }

  return responseData;
}

export async function enableNativePushNotifications() {
  if (!Capacitor.isNativePlatform()) {
    return buildPushPreferenceState({
      supported: false,
      permission: "unsupported",
      optedIn: false,
    });
  }

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const support = await FirebaseMessaging.isSupported();
  if (!support.isSupported) {
    return buildPushPreferenceState({
      supported: false,
      permission: "unsupported",
      optedIn: false,
    });
  }

  let permissions = await FirebaseMessaging.checkPermissions();
  if (permissions.receive !== "granted") {
    permissions = await FirebaseMessaging.requestPermissions();
  }

  const permission = permissions.receive || "prompt";
  const optedIn = permission === "granted";
  writePushOptIn(optedIn);
  notifyPushPreferenceChanged();

  if (optedIn) {
    try {
      const { token } = await FirebaseMessaging.getToken();
      await registerNativeDeviceToken(token, {
        enableRoutineNotifications: true,
      });
    } catch (error) {
      console.warn("Native push token registration will retry in the background", error);
    }
  } else {
    await unregisterCurrentPushToken({ disableRoutineNotifications: true }).catch(
      () => null
    );
  }

  return buildPushPreferenceState({ permission, optedIn });
}

/**
 * Ask once for push notifications after native authentication. Persisting both
 * acceptance and refusal keeps later app launches from overriding the user's
 * explicit choice; the Vault settings switch remains available at any time.
 */
export async function enableNativePushNotificationsAfterAuth() {
  if (!Capacitor.isNativePlatform() || hasStoredPushPreference()) {
    return getNativePushPreferenceState();
  }

  return enableNativePushNotifications();
}

export async function disableNativePushNotifications() {
  if (!Capacitor.isNativePlatform()) {
    return buildPushPreferenceState({
      supported: false,
      permission: "unsupported",
      optedIn: false,
    });
  }

  let notificationPreference = null;
  let unregisterError = null;
  try {
    notificationPreference = await unregisterCurrentPushToken({
      disableRoutineNotifications: true,
    });
  } catch (error) {
    unregisterError = error;
  }
  writePushOptIn(false);
  notifyPushPreferenceChanged();

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const permissions = await FirebaseMessaging.checkPermissions().catch(() => ({
    receive: "prompt",
  }));
  const nextState = buildPushPreferenceState({
    permission: permissions.receive || "prompt",
    optedIn: false,
  });
  if (unregisterError) throw unregisterError;
  return {
    ...nextState,
    chatEmailFrequency: notificationPreference?.chat_email_frequency || null,
  };
}

/**
 * Registers and manages FCM push notifications on native platforms.
 * Web remains on the existing WebSocket notification flow.
 */
export function usePushNotifications(enabled = true) {
  const tokenRef = useRef(null);
  const [optedIn, setOptedIn] = useState(readPushOptIn);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handlePreferenceChanged = () => {
      setOptedIn(readPushOptIn());
    };

    window.addEventListener(PUSH_PREFERENCE_EVENT, handlePreferenceChanged);
    return () => {
      window.removeEventListener(PUSH_PREFERENCE_EVENT, handlePreferenceChanged);
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !enabled || !optedIn) {
      return undefined;
    }

    let mounted = true;
    const listenerHandles = [];

    async function init() {
      try {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        const permResult = await FirebaseMessaging.checkPermissions();
        if (!mounted) {
          await unregisterCurrentPushToken().catch(() => null);
          return;
        }
        if (permResult.receive !== "granted") {
          await unregisterCurrentPushToken({
            disableRoutineNotifications: true,
          }).catch(() => null);
          writePushOptIn(false);
          notifyPushPreferenceChanged();
          return;
        }

        const registerToken = async (token) => {
            if (!mounted || !token) return;
            tokenRef.current = token;
            await registerNativeDeviceToken(token).catch(() => null);
        };

        listenerHandles.push(await FirebaseMessaging.addListener(
          "tokenReceived",
          ({ token }) => registerToken(token)
        ));

        listenerHandles.push(await FirebaseMessaging.addListener(
          "notificationReceived",
          ({ notification }) => {
            const slug = String(notification?.data?.platform_notice || "");
            if (slug) {
              window.dispatchEvent(new CustomEvent("vg:platform-notice-push", { detail: { slug } }));
            }
          }
        ));

        listenerHandles.push(await FirebaseMessaging.addListener(
          "notificationActionPerformed",
          ({ notification }) => {
            const data = notification?.data || {};
            if (data.platform_notice) {
              const slug = String(data.platform_notice);
              try { window.sessionStorage.setItem("vg_platform_notice_pending_push", slug); } catch {}
              window.dispatchEvent(new CustomEvent("vg:platform-notice-push", { detail: { slug } }));
              return;
            }
            if (data.notification_id || data.route || data.action_url) {
              window.dispatchEvent(
                new CustomEvent("vg:push-notification-tapped", { detail: data })
              );
            }
          }
        ));

        // getToken returns the same FCM token format on Android and iOS. The
        // listener handles future token rotation while this registers now.
        const { token } = await FirebaseMessaging.getToken();
        await registerToken(token);
      } catch (error) {
        console.warn("Native push initialization skipped", error);
      }
    }

    init();

    return () => {
      mounted = false;
      listenerHandles.forEach((handle) => handle?.remove?.());
    };
  }, [enabled, optedIn]);

  async function unregisterToken() {
    if (!Capacitor.isNativePlatform() || !tokenRef.current) return;
    await unregisterCurrentPushToken().catch(() => null);
    tokenRef.current = null;
  }

  return { unregisterToken };
}
